import { CHART_COLORS } from '@/constants/colors'
import type { PerformingVault } from '@/utils/calculateBestPerformingVaults'
import type { Network } from '@vaultsfyi/common/types'
import { fromUnixTimestamp } from '@vaultsfyi/common/utils'

export const CHART_COLOR_VALUES = Object.values(CHART_COLORS).map((v) => `rgb(${v})`)

interface ChartDataPoint {
  timestamp: number
  [vaultKey: string]: number | null
}

export interface ChartSerie {
  key: string
  name: string
  network: Network
  vaultId: string
  protocol?: { name: string }
  color: string
  totalChange: number
}

export interface ChartConfig {
  data: ChartDataPoint[]
  series: ChartSerie[]
}

export function transformDataToBestPerformingVaultChartConfig(vaults: PerformingVault[]): ChartConfig {
  if (!vaults || vaults.length === 0) {
    return { data: [], series: [] }
  }

  const timestamps = Array.from(
    new Set(vaults.flatMap((vault) => vault.filteredHistoricalData.map((point) => point.timestamp)))
  ).sort((a, b) => a - b)

  const series: ChartSerie[] = vaults.map((vault, idx) => ({
    key: `${vault.vault.network}:${vault.vault.vaultId}`,
    name: vault.vault.name,
    network: vault.vault.network,
    vaultId: vault.vault.vaultId,
    protocol: vault.vault.protocol,
    color: CHART_COLOR_VALUES[idx % CHART_COLOR_VALUES.length],
    totalChange: vault.sharePriceDiff,
  }))

  const data: ChartDataPoint[] = timestamps.map((timestamp) => {
    const dataPoint: ChartDataPoint = {
      timestamp: fromUnixTimestamp(timestamp),
    }

    for (const vault of vaults) {
      const vaultKey = `${vault.vault.network}:${vault.vault.vaultId}`
      const historicalPoint = vault.filteredHistoricalData.find((p) => p.timestamp === timestamp)

      if (historicalPoint) {
        const percentChange = ((historicalPoint.sharePrice - vault.firstPrice) / vault.firstPrice) * 100
        dataPoint[vaultKey] = percentChange
      } else {
        dataPoint[vaultKey] = interpolateValue(vault.filteredHistoricalData, timestamp, vault.firstPrice)
      }
    }

    return dataPoint
  })

  return { data, series }
}

function interpolateValue(
  historicalData: PerformingVault['filteredHistoricalData'],
  targetTimestamp: number,
  firstPrice: number
): number | null {
  const sortedData = [...historicalData].sort((a, b) => a.timestamp - b.timestamp)

  let before = null
  let after = null

  for (let i = 0; i < sortedData.length; i++) {
    if (sortedData[i].timestamp < targetTimestamp) {
      before = sortedData[i]
    } else if (sortedData[i].timestamp > targetTimestamp) {
      after = sortedData[i]
      break
    }
  }

  if (!before || !after) return null

  const timeDiff = after.timestamp - before.timestamp
  const timeRatio = (targetTimestamp - before.timestamp) / timeDiff
  const priceDiff = after.sharePrice - before.sharePrice
  const interpolatedPrice = before.sharePrice + priceDiff * timeRatio

  return ((interpolatedPrice - firstPrice) / firstPrice) * 100
}
